import { useAdOperatorReportIo } from '@backoffice-frontend/ad-operator-report-io'
import { getAllPassengers } from '@backoffice-frontend/ad-passenger-list'
import {
  useErrorHandlingMutation,
  useErrorHandlingSubscription,
} from '@backoffice-frontend/data-fetching'
import {
  FccAdAlertType,
  FccManageServiceDecision,
  FccPassengerAuthenticationState,
  FccServiceCancellationTypeEnum,
  FccWaypointState,
} from '@backoffice-frontend/graphql'
import { FccManageServiceStatusEnum } from '@backoffice-frontend/graphql/subscription'

import {
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { CreationContext } from '@backoffice-frontend/report-io-lib'
import * as Sentry from '@sentry/react'
import { useMachine } from '@xstate/react'
import { createContext, type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { assign, setup } from 'xstate'
import { useAdVehicleStream } from '../AdVehicleStream/useAdVehicleStream'
import type { AdVehicleStreamServiceCancellationVehicleFragment } from './ManageService.hook'
import { useAdVehicleStreamManageServiceMutation } from './ManageService.hook'
import { useAdVehicleStreamManageServiceProgressSubscription } from './ManageServiceProgress.subscription.hook'

export type ActiveCard =
  | 'serviceCancellation'
  | 'restoreMission'
  | 'tripSelection'
  | 'emergencyStop'
  | null

type ServiceCancellationValue = {
  activeCard: ActiveCard
  state: ServiceCancellationMachineState
  isExecuting: boolean
  isTripManagementAllowed: boolean
  restoreMission: (vehicleId: string) => void
  cancelService: (vehicleId: string) => void
  manageTrips: (vehicleId: string) => void
  continueService: (vehicleId: string, incidentId?: string) => void
  confirmCancelAllTrips: () => void
  confirmCancelSomeTrips: (tripIds: string[]) => void
  confirmContinue: (incidentId?: string) => void
  confirmRestoreMission: () => void
  abandon: () => void
}

export const ManageServiceContext = createContext<
  ServiceCancellationValue | undefined
>(undefined)

type ManageServiceProviderProps = {
  vehicle: AdVehicleStreamServiceCancellationVehicleFragment
  children: ReactNode
}

type MachineContext = {
  activeCard: ActiveCard
  decision?: FccManageServiceDecision
  cancellationType?: FccServiceCancellationTypeEnum
  tripIds?: string[]
  incidentId?: string
  sessionId?: string
}

const manageServiceMachine = setup({
  types: {
    events: {} as
      | {
          type: 'SHOW_CARD'
          card: 'serviceCancellation' | 'restoreMission' | 'tripSelection'
        }
      | {
          type: 'EXECUTE'
          decision: FccManageServiceDecision
          cancellationType: FccServiceCancellationTypeEnum
          tripIds?: string[]
          incidentId?: string
        }
      | { type: 'MUTATION_SUCCESS'; sessionId: string }
      | { type: 'MUTATION_ERROR' }
      | { type: 'PROGRESS_PENDING'; sessionId: string }
      | { type: 'PROGRESS_COMPLETED' }
      | { type: 'PROGRESS_FAILED' }
      | { type: 'ABANDON' },
    context: {} as MachineContext,
  },
  actions: {
    resetContext: () => {},
    onSuccess: () => {},
    onError: () => {},
  },
}).createMachine({
  initial: 'idle',
  context: {
    activeCard: null,
    decision: undefined,
    cancellationType: undefined,
    tripIds: undefined,
    incidentId: undefined,
    sessionId: undefined,
  },
  states: {
    idle: {
      entry: 'resetContext',
      on: {
        SHOW_CARD: {
          target: 'showingCard',
          actions: assign(({ event }) => ({
            activeCard: event.card,
          })),
        },
        EXECUTE: {
          target: 'executing',
          actions: assign(({ event }) => ({
            decision: event.decision,
            cancellationType: event.cancellationType,
            tripIds: event.tripIds,
            incidentId: event.incidentId,
            activeCard: null,
          })),
        },
        PROGRESS_PENDING: {
          target: 'awaitingResult',
          actions: assign(({ event }) => ({
            sessionId: event.sessionId,
          })),
        },
      },
    },

    showingCard: {
      on: {
        EXECUTE: {
          target: 'executing',
          actions: assign(({ event }) => ({
            decision: event.decision,
            cancellationType: event.cancellationType,
            tripIds: event.tripIds,
            incidentId: event.incidentId,
          })),
        },
        ABANDON: 'idle',
      },
    },

    executing: {
      on: {
        MUTATION_SUCCESS: {
          target: 'awaitingResult',
          actions: assign(({ event }) => ({
            sessionId: event.sessionId,
          })),
        },
        MUTATION_ERROR: 'failed',
        ABANDON: 'idle',
      },
    },

    awaitingResult: {
      on: {
        PROGRESS_COMPLETED: 'completed',
        PROGRESS_FAILED: 'failed',
        ABANDON: 'idle',
      },
    },

    completed: {
      entry: 'onSuccess',
      always: 'idle',
    },

    failed: {
      entry: 'onError',
      always: 'idle',
    },
  },
})

export type ServiceCancellationMachineState = ReturnType<
  typeof useMachine<typeof manageServiceMachine>
>[0]

export const ManageServiceProvider = ({
  vehicle,
  children,
}: ManageServiceProviderProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { openReportDialog } = useAdOperatorReportIo()
  const { isHandledByCurrentUser } = useAdVehicleStream()

  const [manageServiceMutation] = useErrorHandlingMutation(
    useAdVehicleStreamManageServiceMutation,
  )

  const emergencyStopAlertId = vehicle.fccCurrentAdAlerts?.alerts.find(
    alert =>
      alert.type === FccAdAlertType.EmergencyStop && alert.status !== 'CLOSED',
  )?.id

  const [state, send] = useMachine(
    manageServiceMachine.provide({
      actions: {
        resetContext: assign(() => ({
          activeCard: null as ActiveCard,
          decision: undefined,
          cancellationType: undefined,
          tripIds: undefined,
          incidentId: undefined,
          sessionId: undefined,
        })),
        onSuccess: ({ context }) => {
          if (
            context.decision === FccManageServiceDecision.CancelAllTrips ||
            context.decision === FccManageServiceDecision.CancelSomeTrips
          ) {
            enqueueMoiaSuccessNotification(
              context.decision === FccManageServiceDecision.CancelSomeTrips &&
                context.tripIds?.length === 1
                ? t('adFleetMap_toast_tripActionSuccessful_caption')
                : t('adFleetMap_toast_tripsCancelled_caption'),
              { duration: 4000 },
            )
            setTimeout(() => {
              openReportDialog({
                vehicleId: vehicle.id,
                creationContext: CreationContext.FleetMapServiceCancellation,
              })
            }, 1000)
          } else if (context.decision === FccManageServiceDecision.Continue) {
            enqueueMoiaSuccessNotification(
              t('adFleetMap_toast_continueAllTripsSuccess_label'),
              { duration: 4000 },
            )
          } else if (
            context.decision === FccManageServiceDecision.RestoreMission
          ) {
            enqueueMoiaSuccessNotification(
              t('adFleetMap_missionHasBeenRestored_label'),
              { duration: 4000 },
            )
          }
        },
        onError: ({ context }) => {
          if (context.decision === FccManageServiceDecision.CancelAllTrips) {
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_cancellingServiceError_label'),
            )
          } else if (
            context.decision === FccManageServiceDecision.CancelSomeTrips
          ) {
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_tripActionsFailed_caption'),
              { title: t('adFleetMap_toast_tripActionsFailed_header') },
            )
          } else if (context.decision === FccManageServiceDecision.Continue) {
            enqueueMoiaErrorNotification(
              t('adFleetMap_toast_continueAllTripsFailed_caption'),
            )
          }
        },
      },
    }),
  )

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (!isHandledByCurrentUser && !stateRef.current.matches('idle')) {
      send({ type: 'ABANDON' })
    }
  }, [isHandledByCurrentUser, send])

  // Always-on subscription for progress tracking + page refresh recovery
  const { data: progressData } = useErrorHandlingSubscription(
    useAdVehicleStreamManageServiceProgressSubscription,
    { variables: { vehicleId: vehicle.id } },
  )

  useEffect(() => {
    if (!progressData?.fccManageServiceProgress) return
    const {
      status: progressStatus,
      sessionId,
      error,
    } = progressData.fccManageServiceProgress

    switch (progressStatus) {
      case FccManageServiceStatusEnum.Pending:
        if (stateRef.current.matches('idle')) {
          send({ type: 'PROGRESS_PENDING', sessionId })
        }
        break
      case FccManageServiceStatusEnum.Completed:
        if (!error) {
          send({ type: 'PROGRESS_COMPLETED' })
        } else {
          send({ type: 'PROGRESS_FAILED' })
        }
        break
      case FccManageServiceStatusEnum.Failed:
      case FccManageServiceStatusEnum.Timeout:
        send({ type: 'PROGRESS_FAILED' })
        break
    }
  }, [progressData, send])

  const passengers = getAllPassengers(vehicle.fccUpcomingWaypoints ?? [])
  const nextStopId = vehicle.fccUpcomingWaypoints?.at(0)?.fccStop?.id
  const isWaypointReached =
    vehicle.fccUpcomingWaypoints?.at(0)?.adState === FccWaypointState.Reached

  const isAtCurrentReachedStop = (passenger: {
    fccStop?: { id: string | null } | null
  }) => isWaypointReached && passenger.fccStop?.id === nextStopId

  const isTripManagementAllowed = passengers.some(
    passenger =>
      (passenger.pickedUpAt !== null &&
        passenger.nextWaypointActionType !== 'PICKUP' &&
        !isAtCurrentReachedStop(passenger)) ||
      (passenger.authenticationState ===
        FccPassengerAuthenticationState.Upcoming &&
        !isAtCurrentReachedStop(passenger)),
  )

  const executeManageService = (
    decision: FccManageServiceDecision,
    cancellationType: FccServiceCancellationTypeEnum,
    tripIds?: string[],
    incidentId?: string,
  ) => {
    send({
      type: 'EXECUTE',
      decision,
      cancellationType,
      tripIds,
      incidentId,
    })
    manageServiceMutation({
      variables: {
        input: {
          vehicleId: vehicle.id,
          cancellationType,
          decision,
          ...(tripIds && { tripIds }),
          ...(incidentId && { incidentId }),
        },
      },
      onCompleted: data => {
        send({
          type: 'MUTATION_SUCCESS',
          sessionId: data.fccManageService.sessionId,
        })
      },
      onError: () => {
        send({ type: 'MUTATION_ERROR' })
      },
    })
  }

  const restoreMission = (vehicleId: string) => {
    if (vehicleId !== vehicle.id) {
      reportVehicleMismatch(
        vehicleId,
        vehicle.id,
        enqueueMoiaErrorNotification,
        t,
      )
      return
    }
    send({ type: 'SHOW_CARD', card: 'restoreMission' })
  }

  const cancelService = (vehicleId: string) => {
    if (vehicleId !== vehicle.id) {
      reportVehicleMismatch(
        vehicleId,
        vehicle.id,
        enqueueMoiaErrorNotification,
        t,
      )
      return
    }
    send({ type: 'SHOW_CARD', card: 'serviceCancellation' })
  }

  const manageTrips = (vehicleId: string) => {
    if (vehicleId !== vehicle.id) {
      reportVehicleMismatch(
        vehicleId,
        vehicle.id,
        enqueueMoiaErrorNotification,
        t,
      )
      return
    }
    send({ type: 'SHOW_CARD', card: 'tripSelection' })
  }

  const continueService = (vehicleId: string, incidentId?: string) => {
    if (vehicleId !== vehicle.id) {
      reportVehicleMismatch(
        vehicleId,
        vehicle.id,
        enqueueMoiaErrorNotification,
        t,
      )
      return
    }
    executeManageService(
      FccManageServiceDecision.Continue,
      FccServiceCancellationTypeEnum.AllTrips,
      undefined,
      incidentId,
    )
  }

  const confirmCancelAllTrips = () => {
    executeManageService(
      FccManageServiceDecision.CancelAllTrips,
      FccServiceCancellationTypeEnum.AllTrips,
      undefined,
      emergencyStopAlertId,
    )
  }

  const confirmCancelSomeTrips = (tripIds: string[]) => {
    executeManageService(
      FccManageServiceDecision.CancelSomeTrips,
      FccServiceCancellationTypeEnum.SomeTrips,
      tripIds,
      emergencyStopAlertId,
    )
  }

  const confirmContinue = (incidentId?: string) => {
    executeManageService(
      FccManageServiceDecision.Continue,
      FccServiceCancellationTypeEnum.AllTrips,
      undefined,
      incidentId ?? emergencyStopAlertId,
    )
  }

  const confirmRestoreMission = () => {
    executeManageService(
      FccManageServiceDecision.RestoreMission,
      FccServiceCancellationTypeEnum.AllTrips,
    )
  }

  const isExecuting =
    state.matches('executing') || state.matches('awaitingResult')

  return (
    <ManageServiceContext.Provider
      value={{
        activeCard: state.context.activeCard,
        state,
        isExecuting,
        isTripManagementAllowed,
        restoreMission,
        cancelService,
        manageTrips,
        continueService,
        confirmCancelAllTrips,
        confirmCancelSomeTrips,
        confirmContinue,
        confirmRestoreMission,
        abandon: () => send({ type: 'ABANDON' }),
      }}
    >
      {children}
    </ManageServiceContext.Provider>
  )
}

function reportVehicleMismatch(
  vehicleId: string,
  expectedId: string,
  enqueueMoiaErrorNotification: (msg: string) => void,
  t: (key: string) => string,
) {
  Sentry.captureException(
    new Error(
      `Mismatch of vehicleIds during attempted service cancellation. Selected vehicle from props: ${expectedId}, vehicleId from parameter: ${vehicleId}`,
    ),
  )
  enqueueMoiaErrorNotification(
    t('adFleetMap_toast_cancellingServiceError_label'),
  )
}
